import { useEffect, useState } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import { getOrganizationReport } from "../../api/organizationApi";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const COLORS = {
  blue:   "#4f81ee",
  purple: "#7C3AED",
  pink:   "#EC4899",
  teal:   "#14B8A6",
  orange: "#F97316",
};

/* ─── Gender Pie Chart with zero-guard ─── */
const PIE_BG = [COLORS.blue, COLORS.pink, COLORS.purple];

const GenderPieChart = ({ labels, data }) => {
  // Remove slices where count === 0 before handing to Chart.js.
  // Chart.js renders a completely blank/invisible circle when every value is 0.
  const filtered = labels.reduce(
    (acc, label, i) => {
      if (data[i] > 0) {
        acc.labels.push(label);
        acc.data.push(data[i]);
        acc.colors.push(PIE_BG[i] || COLORS.orange);
      }
      return acc;
    },
    { labels: [], data: [], colors: [] }
  );

  const total = filtered.data.reduce((a, b) => a + b, 0);

  if (total === 0) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "220px", color: "#9ca3af", gap: "8px",
      }}>
        <span style={{ fontSize: "36px" }}>⚥</span>
        <p style={{ fontSize: "13px" }}>No gender data available.</p>
        <p style={{ fontSize: "11px", color: "#d1d5db" }}>
          Assign gender to employees to see this chart.
        </p>
      </div>
    );
  }

  return (
    <div>
      <Pie
        data={{
          labels: filtered.labels,
          datasets: [{
            data: filtered.data,
            backgroundColor: filtered.colors,
            borderWidth: 2,
            borderColor: "#fff",
          }],
        }}
        options={{
          responsive: true,
          plugins: {
            legend: { position: "bottom" },
            tooltip: {
              callbacks: {
                label: (ctx) =>
                  ` ${ctx.label}: ${ctx.raw} (${Math.round((ctx.raw / total) * 100)}%)`,
              },
            },
          },
        }}
      />
      <div style={{
        display: "flex", justifyContent: "center", gap: "16px",
        marginTop: "12px", flexWrap: "wrap",
      }}>
        {filtered.labels.map((label, i) => (
          <div key={label} style={{
            display: "flex", alignItems: "center", gap: "6px",
            fontSize: "13px", color: "#374151",
          }}>
            <span style={{
              width: "10px", height: "10px", borderRadius: "50%",
              background: filtered.colors[i], display: "inline-block",
            }} />
            {label}: <strong>{filtered.data[i]}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Summary Card ─── */
const SummaryCard = ({ title, value }) => (
  <div className="bg-white p-4 rounded-xl shadow text-center">
    <p className="text-gray-500 text-sm">{title}</p>
    <p className="text-2xl font-bold text-gray-800">{value ?? "—"}</p>
  </div>
);

/* ─── Main Component ─── */
const OrganizationReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getOrganizationReport();
      setReport(res.data);
    } catch (err) {
      console.error(err);
      setError("Unable to load organization report");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="text-gray-500">Loading report...</p>;
  if (error)   return <p className="text-red-500">{error}</p>;
  if (!report) return <p>No report data available</p>;

  return (
    <div className="space-y-8">

      {/* Refresh */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={fetchReport}
          style={{
            background: "#f3f4f6", border: "1px solid #e5e7eb",
            borderRadius: "8px", padding: "6px 14px",
            fontSize: "13px", color: "#374151", cursor: "pointer",
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard title="Current Employees"  value={report.summary.current_employees} />
        <SummaryCard title="Relieved Employees" value={report.summary.relieved_employees} />
        <SummaryCard title="Male"               value={report.summary.male_employees} />
        <SummaryCard title="Female"             value={report.summary.female_employees} />
        <SummaryCard
          title="Average Age"
          value={report.summary.average_age ? `${report.summary.average_age} yrs` : "—"}
        />
      </div>

      {/* ── CHARTS ── */}
      <div className="grid md:grid-cols-2 gap-6">

        <div className="bg-white p-5 rounded-xl shadow">
          <h3 className="font-semibold mb-4 text-gray-700">Gender Distribution</h3>
          <GenderPieChart
            labels={report.gender_chart.labels}
            data={report.gender_chart.data}
          />
        </div>

        <div className="bg-white p-5 rounded-xl shadow">
          <h3 className="font-semibold mb-4 text-gray-700">Age Distribution</h3>
          <Bar
            data={{
              labels: report.age_chart.labels,
              datasets: [{
                label: "Employees",
                data: report.age_chart.data,
                backgroundColor: COLORS.teal,
                borderRadius: 6,
              }],
            }}
            options={{
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
            }}
          />
        </div>
      </div>

      {/* ── DEPARTMENT TABLE ── */}
      <div className="bg-white p-5 rounded-xl shadow">
        <h3 className="font-semibold mb-4 text-gray-700">Department-wise Employees</h3>

        {report.department_chart.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">No department data.</p>
        ) : (
          <table className="w-full text-sm border rounded overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 text-left">Department</th>
                <th className="border p-2 text-left">Count</th>
              </tr>
            </thead>
            <tbody>
              {report.department_chart.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="border p-2">{row.department || "Not Assigned"}</td>
                  <td className="border p-2 font-medium">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
};

export default OrganizationReport;
